import {
  applyInferredDocumentShape,
  getInitialTemplate,
} from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import Http from 'services/Http';
import Attachments from 'services/api/Attachments';
import authCompanyId from 'store/selectors/authCompanyId';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';

const PLACEHOLDER_SIGNER_IDS = [
  '507f191e810c19729de860e0',
  '507f191e810c19729de860e1',
  '507f191e810c19729de860e2',
  '507f191e810c19729de860e3',
  '507f191e810c19729de860e4',
  '507f191e810c19729de860e5',
];

/**
 * Shared template + PDF canvas state for edit drawer and send drawer setup steps.
 * @param {{ format: object|null, defaultMessage?: string, resetKey?: string|number, realMembers?: Array }} args
 * When `resetKey` is set, initial state is recomputed only when it changes (not on every parent `format` object identity).
 * When `realMembers` is provided (non-empty), it is used in place of the generic "Signer N" placeholder list.
 * When `skipAttachmentAutoLoad` is true, saved template/layout attachments are not fetched into the canvas
 * until the user uploads a PDF (send drawer per-send: ask for this batch's file first).
 */
export default function useTemplateEditor({
  format,
  defaultMessage,
  resetKey,
  realMembers,
  skipAttachmentAutoLoad = false,
}) {
  const companyId = useSelector(authCompanyId);
  /**
   * PDFs from the last multi-file drop (same order as selected). First file drives canvas + attachment id;
   * full list seeds ExtractedRecipients so each upload is parsed separately (HrBot-style), not one merged PDF.
   */
  const sessionPdfFilesRef = useRef(null);
  const [template, setTemplate] = useState({});
  const [fields, setFields] = useState([]);
  const [canvasPdfSrc, setCanvasPdfSrc] = useState(null);
  const [currentSignerIndex, setCurrentSignerIndex] = useState(0);
  const [uploadBusy, setUploadBusy] = useState(false);

  const placeholderMembers = useMemo(
    () =>
      realMembers?.length
        ? realMembers
        : PLACEHOLDER_SIGNER_IDS.map((id, i) => ({
            firstName: I18n.t('HrBot.EditTemplate.signerLabel', { n: i + 1 }),
            surName: '',
            user: { _id: id },
          })),
    [realMembers],
  );

  const routingItemsCount = (template.items || []).length;

  const formatResetKey =
    resetKey ??
    (format?._id
      ? String(format._id)
      : [format?.templateAttachment, format?.layoutSampleAttachment].filter(Boolean).join('-') ||
        'empty');

  useEffect(() => {
    const initial = getInitialTemplate(format, defaultMessage || I18n.t('HrBot.defaultMessage'));
    setTemplate(initial);
    setFields(initial.fields || []);
    setCanvasPdfSrc(null);
    setCurrentSignerIndex(0);
    sessionPdfFilesRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when logical template identity changes
  }, [formatResetKey, defaultMessage]);

  useEffect(() => {
    setTemplate(t => {
      const patch = applyInferredDocumentShape(t);
      const next = { ...t, ...patch };
      if (
        next.documentShape === t.documentShape &&
        next.split === t.split &&
        next.documentSource === t.documentSource
      ) {
        return t;
      }
      return next;
    });
  }, [template.type, routingItemsCount]);

  // onePerMember: all widgets belong to signer 0 (single colour in the editor).
  // oneForAll: advance currentSignerIndex to the next placeholder that still needs a signature field.
  useEffect(() => {
    if (template.signingMode === 'onePerMember') {
      setCurrentSignerIndex(0);
      return;
    }
    const n = placeholderMembers.length;
    if (!n) return;
    const sigFields = (fields || []).filter(f => f?.type === 'signature');
    for (let i = 0; i < n; i += 1) {
      const hasSig = sigFields.some(f => (f.signerIndex ?? 0) === i);
      if (!hasSig) {
        setCurrentSignerIndex(i);
        return;
      }
    }
  }, [template.signingMode, placeholderMembers.length, fields]);

  useEffect(() => {
    let cancelled = false;
    if (skipAttachmentAutoLoad) return () => {};
    const activeAttachmentId =
      template.documentSource === 'fixed'
        ? template.templateAttachment
        : template.layoutSampleAttachment || template.templateAttachment;
    if (!canvasPdfSrc && activeAttachmentId) {
      Http.get(`/v1/attachment/${activeAttachmentId}`)
        .then(body => {
          const link = body?.data?.attachment?.httpLink;
          if (!cancelled && link) {
            setCanvasPdfSrc(link);
          }
        })
        .catch(() => null);
    }
    return () => {
      cancelled = true;
    };
  }, [
    canvasPdfSrc,
    template.documentSource,
    template.layoutSampleAttachment,
    template.templateAttachment,
    skipAttachmentAutoLoad,
  ]);

  const syncFieldsToTemplate = useCallback(nextFields => {
    setFields(nextFields);
    setTemplate(t => ({ ...t, fields: nextFields }));
  }, []);

  useEffect(() => {
    if (template.signingMode !== 'onePerMember') return;
    const needsNormalize = (fields || []).some(f => (f.signerIndex ?? 0) !== 0);
    if (!needsNormalize) return;
    syncFieldsToTemplate((fields || []).map(f => ({ ...f, signerIndex: 0 })));
  }, [template.signingMode, fields, syncFieldsToTemplate]);

  const onPdfDrop = useCallback(
    async (acceptedFiles, attachmentKey) => {
      const list = (acceptedFiles || []).filter(Boolean);
      if (!list.length || !companyId) return;
      const primary = list[0];
      sessionPdfFilesRef.current = list;
      setUploadBusy(true);
      try {
        const { _id } = await Attachments.upload(
          companyId,
          'none',
          primary,
          null,
          null,
          null,
          null,
        );
        setTemplate(t => ({ ...t, [attachmentKey]: _id }));
        setCanvasPdfSrc(URL.createObjectURL(primary));
      } finally {
        setUploadBusy(false);
      }
    },
    [companyId],
  );

  const onReplacePdf = useCallback(() => {
    sessionPdfFilesRef.current = null;
    setTemplate(t => ({
      ...t,
      ...(t.documentSource === 'fixed'
        ? { templateAttachment: null }
        : { layoutSampleAttachment: null }),
    }));
    setCanvasPdfSrc(null);
  }, []);

  return {
    template,
    setTemplate,
    fields,
    setFields,
    syncFieldsToTemplate,
    canvasPdfSrc,
    setCanvasPdfSrc,
    uploadBusy,
    onPdfDrop,
    onReplacePdf,
    currentSignerIndex,
    setCurrentSignerIndex,
    placeholderMembers,
    sessionPdfFilesRef,
  };
}
